// Renders the server-built Quotation View (services/quotation_view.py) for the saved page and Link. Layout only — no maths.
import React from 'react';
import { MiscSummaryTable } from './MiscSummaryTable';
import { C, Card, Group, SimpleTable, Bullets } from './ViewParts';
import { TripSummary, TourRoute, SketchItinerary, HotelsByCategory, VehicleFacts, DayWise, InclusionsExclusions } from './ViewSections';
import { formatItineraryTitle } from '../QuotationDisplay/displayConfig';
import { Car, ArrowLeftRight, Receipt, PlusCircle, IndianRupee, CreditCard, ScrollText, Landmark, Building2 } from 'lucide-react';
import { ContactRow } from './ContactRow';

const Vehicles = ({ v }) => (
  <>
    <VehicleFacts v={v} />
    {v.options.length > 1 && (
      <Card id="vehicles" title="Vehicle Upgrade Options" icon={Car}>
        <SimpleTable head={['Vehicle', 'No. of Vehicles', 'Uplift']} rightFrom={2} testid="view-vehicle-options"
          rows={v.options.map((o, i) => [
            <span key="n" className="font-medium">{o.name}{o.is_base && <span className="ml-2 text-[10px] rounded bg-[color-mix(in_srgb,var(--navy)_10%,white)] text-[var(--navy)] px-1.5 py-0.5 uppercase">Base</span>}</span>,
            <span key="c" data-testid={`view-vehicle-count-${i}`}>{o.count}</span>,
            <span key="u" className="text-[var(--navy)]">{o.uplift.text}</span>])} />
      </Card>
    )}
  </>
);

// First column stays visible while the rest scrolls sideways on small screens.
const STICKY = 'sticky left-0 z-[1]';

const PriceTable = ({ t, fmt }) => (
  <Group head={t.label || (fmt === 'per_person' ? 'Per Person' : 'Total Package')} testId={`view-price-table-${t.meal_plan || 'all'}`}>
    <div className="overflow-x-auto">
      <table className="w-full min-w-max md:min-w-0" data-testid="view-price-table">
        <thead>
          <tr className="bg-gray-100">
            {t.columns.map((c, i) => (
              <th key={c.key} className={`${C.th} border-b border-[#E5E7EB] ${c.align === 'right' ? 'text-right' : 'text-left'} ${i === 0 ? `${STICKY} bg-gray-100` : ''} ${c.grand ? 'bg-[color-mix(in_srgb,var(--navy)_15%,white)] !text-[var(--navy)]' : ''} ${c.pax ? 'bg-[color-mix(in_srgb,var(--navy)_10%,white)] !text-[var(--navy)]' : ''}`}>
                {c.label}{c.sub && <><br /><span className="font-normal">{c.sub}</span></>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.rows.map((r) => (
            <tr key={r.category}>
              {t.columns.map((c, i) => (
                <td key={c.key} data-testid={c.grand ? `grand-total-${r.category}-${r.meal_plan}` : undefined}
                  className={`${C.td} border-b border-[#E5E7EB] ${c.align === 'right' ? 'text-right whitespace-nowrap' : 'font-medium'} ${i === 0 ? `${STICKY} bg-white` : ''} ${c.pax || c.key === 'total' ? 'font-semibold !text-[var(--navy)]' : ''} ${c.key === 'gst' ? '!text-gray-600' : ''} ${c.grand ? 'font-bold !text-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_10%,white)]' : ''}`}>
                  {r.cells[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Group>
);

const PriceSummary = ({ t }) => t.summary && (
  <Group head={`Summary${t.label ? ` – ${t.label}` : ''}`}>
    <MiscSummaryTable bare title={null} testId={`view-misc-summary-${t.meal_plan || 'all'}`}
      rows={t.summary.map((r) => ({ option: r.option, package: r.package.text, misc: r.misc.text, grand: r.grand_total.text }))} />
  </Group>
);

const Pricing = ({ p }) => p.tables.length > 0 && (
  <Card id="pricing" title={p.title} icon={IndianRupee} sub={p.gst_text}>
    <div className="space-y-4">
    {p.tables.map((t) => <React.Fragment key={t.meal_plan || 'p'}><PriceTable t={t} fmt={p.format} /><PriceSummary t={t} /></React.Fragment>)}
    {[...p.missing.map((m) => `Note: ${m.text}`), ...p.notes].map((n) => (
      <p key={n} className="px-3 py-2 text-sm text-[var(--navy)] bg-[color-mix(in_srgb,var(--cyan)_10%,white)] border border-[color-mix(in_srgb,var(--navy)_15%,transparent)] rounded break-words">{n}</p>
    ))}
    </div>
  </Card>
);

const Misc = ({ m }) => ['included', 'optional'].filter((k) => m[k].length > 0).map((k) => (
  <Card key={k} id={`misc-${k}`} title={k === 'included' ? 'Included Extras' : 'Optional Extras'} icon={k === 'included' ? Receipt : PlusCircle}>
    <SimpleTable head={['Item', 'Basis', 'Rate', 'Qty', 'GST', 'Amount']} rightFrom={2}
      rows={[...m[k].map((x) => [x.label, x.basis, x.rate.text, x.quantity, x.gst.text, x.total.text]),
        [<b key="t">Total</b>, '', '', '', '', <b key="v" data-testid={`misc-${k}-total`}>{m[`${k}_total`].text}</b>]]} />
    {k === 'optional' && m.optional_note && <p className={`${C.label} mt-2`}>{m.optional_note}</p>}
  </Card>
));

const ScheduleGrid = ({ sc }) => {
  const cols = sc.columns.map((c) => (c.type === 'balance' ? `${c.label} ${c.due_text}` : `${c.label} (${c.share_text}) ${c.due_text}`));
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max md:min-w-0" data-testid="view-payment-schedule">
        <thead><tr className="border-b border-[#E5E7EB] text-left">
          {['Category', 'Meal Plan', 'Total', ...cols].map((h, i) => <th key={h} className={`${C.th} ${i >= 2 ? 'text-right' : ''}`}>{h}</th>)}
        </tr></thead>
        <tbody>
          {sc.groups.map((g) => g.rows.map((r, i) => (
            <tr key={`${g.category}-${r.meal_plan}`} className="border-b border-[#F3F4F6]" data-testid={`schedule-row-${g.category}-${r.meal_plan}`}>
              {i === 0 && <td rowSpan={g.rows.length} className={`${C.td} align-top font-semibold !text-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_4%,white)]`}>{g.category}</td>}
              <td className={C.td}>{r.meal_plan}</td>
              <td className={`${C.td} text-right font-semibold whitespace-nowrap`}>{r.total.text}</td>
              {r.instalments.map((a, k) => <td key={k} className={`${C.td} text-right whitespace-nowrap`}>{a.text}</td>)}
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  );
};

const Schedule = ({ sc }) => {
  if (!sc) return null;
  if (sc.rows.length > 1) return <Card id="schedule" title="Payment Policy" icon={CreditCard} right={sc.policy_name}><ScheduleGrid sc={sc} /></Card>;
  const rows = [...sc.columns.map((c, i) => [i + 1, c.label, c.due_text, c.share_text, sc.rows[0].instalments[i].text]),
    ['', <b key="t">Total</b>, '', '', <b key="v">{sc.rows[0].total.text}</b>]];
  return (
    <Card id="schedule" title="Payment Policy" icon={CreditCard} right={sc.policy_name}>
      <SimpleTable head={['#', 'Instalment', 'Due', 'Type', 'Amount']} rows={rows} rightFrom={4} testid="view-payment-schedule" />
    </Card>
  );
};

const Sub = ({ title, children, testId }) => (
  <div data-testid={testId}><p className="text-sm font-semibold text-[var(--navy)] mb-1.5">{title}</p>{children}</div>
);

const CancellationTable = ({ rows, i }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-max md:min-w-0 border border-[#E5E7EB]" data-testid={`cancellation-table-${i}`}>
      <thead><tr className="bg-gray-50">{rows[0].map((c, k) => <th key={k} className={`${C.th} border border-[#E5E7EB] ${k ? 'text-center' : 'text-left'}`}>{c}</th>)}</tr></thead>
      <tbody>{rows.slice(1).map((r, ri) => <tr key={ri}>{r.map((c, k) => <td key={k} className={`${C.td} border border-[#E5E7EB] ${k ? 'text-center whitespace-nowrap' : ''}`}>{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

const Terms = ({ s }) => (s.payment_terms.length > 0 || s.cancellation.length > 0 || s.terms.length > 0) && (
  <Card id="terms" title="Terms & Conditions" icon={ScrollText}>
    <div className="space-y-4">
      {s.payment_terms.length > 0 && <Sub title="Payment Terms"><Bullets items={s.payment_terms} /></Sub>}
      {s.cancellation_blocks?.length > 0 && (
        <Sub title="Cancellation Policy" testId="view-cancellation">
          <div className="space-y-2">
            {s.cancellation_blocks.map((b, i) => (b.type === 'table' ? <CancellationTable key={i} rows={b.rows} i={i} /> : <Bullets key={i} items={b.items} />))}
          </div>
        </Sub>
      )}
      {s.terms.length > 0 && <Sub title="General Terms"><Bullets items={s.terms} /></Sub>}
    </div>
  </Card>
);

const Bank = ({ b }) => b && b.show_bank !== false && (
  <Card id="bank" title="Payment Details" icon={Landmark}>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[['Account Name', b.account_name], ['Bank Name', b.bank_name], ['Account Number', b.account_number], ['IFSC', b.ifsc_code],
        ['Branch', b.branch], ['UPI', b.show_qr !== false ? b.upi_id : '']].filter(([, x]) => x).map(([k, x]) => (
        <div key={k} className="min-w-0"><p className={C.label}>{k}</p><p className={`${C.body} font-semibold break-words`}>{x}</p></div>
      ))}
    </div>
  </Card>
);

const SECTIONS = {
  trip: (s) => <TripSummary t={s.trip} />,
  route: (s) => <TourRoute stops={s.route.stops} />,
  sketch_itinerary: (s) => <SketchItinerary days={s.sketch_itinerary.map((d) => ({ ...d, highlights: formatItineraryTitle(d.highlights) }))} />,
  hotels: (s) => <HotelsByCategory groups={s.hotels} />,
  vehicles: (s) => <Vehicles v={s.vehicles} />,
  p2p: (s) => s.p2p.length > 0 && (
    <Card id="p2p" title="Point-to-Point Transfers" icon={ArrowLeftRight}>
      <SimpleTable head={['Route', 'Vehicle', 'Basis', 'Type', 'Amount']} rightFrom={4}
        rows={s.p2p.map((t) => [t.route, t.vehicle, t.basis, t.included ? 'Included' : 'Optional', t.total.text])} />
    </Card>
  ),
  misc: (s) => <Misc m={s.misc} />,
  pricing: (s) => <Pricing p={s.pricing} />,
  detailed_itinerary: (s) => <DayWise days={s.detailed_itinerary.map((d) => ({ ...d, title: formatItineraryTitle(d.title) }))} />,
  inclusions_exclusions: (s) => <InclusionsExclusions ie={s.inclusions_exclusions} />,
  payment_schedule: (s) => <Schedule sc={s.payment_schedule} />,
  terms: (s) => <Terms s={s} />,
  bank: (s) => <Bank b={s.bank} />,
  contact: (s) => (
    <Card id="contact" title={s.contact.agency_name || 'Contact'} icon={Building2}>
      <ContactRow c={s.contact} />
    </Card>
  ),
  footer: (s) => s.footer.note && <p className={`text-center ${C.label}`}>{s.footer.note}</p>,
};

export const QuotationViewDoc = ({ view }) => {
  if (!view) return null;
  const s = view.sections;
  return (
    <div className="space-y-4 sm:space-y-6 min-w-0 break-words" data-testid="quotation-view-doc">
      {view.order.filter((b) => SECTIONS[b]).map((b) => <React.Fragment key={b}>{SECTIONS[b](s)}</React.Fragment>)}
    </div>
  );
};

export default QuotationViewDoc;
